import React from 'react';
import { Link } from 'react-router';
import { useI18n } from '../../i18n/I18nProvider';
import { useHelp } from '../../help/HelpContext';
import VismaLogo from './VismaLogo';
import './legal.css';

/**
 * The quiet footer under every in-shell page (spec 4.12; privacy plan item 8): the Visma
 * wordmark, black on white with its clear space, the trademark line Visma requires, word
 * for word, then the legal pages, Help and the team note. No gradient: the page's one
 * brand moment is not spent on the footer.
 *
 * On the sign-in page (variant "login") it also carries the page's one big line, a
 * two-tone sentence on the left edge.
 *
 * In the page flow, never fixed, so it covers nothing; it leaves the corner free for the
 * quiet mode bubble (legal.css, :root[data-quiet-bubble]).
 */
export default function AppFooter({ variant = 'page' }: { variant?: 'page' | 'login' }) {
  const { t } = useI18n();
  const help = useHelp();
  const canOpenHelp = !!help?.meta?.user;
  return (
    <footer className={`kfoot kfoot--${variant}`}>
      {variant === 'login' && (
        <p className="kfoot__statement">
          {t('legal.footerStatement')} <span className="kfoot__statement-muted">{t('legal.footerStatementMuted')}</span>
        </p>
      )}
      <div className="kfoot__row">
        <div className="kfoot__brand">
          <span className="kfoot__logo"><VismaLogo className="kfoot__wordmark" height={16} /></span>
          <p className="kfoot__legal">
            © Visma and the Visma logo are registered trademarks of Visma AS. All other trademarks are the property of their respective owners.
          </p>
        </div>
        <nav className="kfoot__links" aria-label={t('legal.footerLabel')}>
          <Link to="/legal/privacy">{t('legal.privacy')}</Link>
          <Link to="/legal/terms">{t('legal.terms')}</Link>
          {canOpenHelp && (
            <button type="button" className="kfoot__help" onClick={() => help!.actions.openPanel()}>
              {t('legal.footerHelp')}
            </button>
          )}
          <span className="kfoot__note">{t('app.footerNote')}</span>
        </nav>
      </div>
    </footer>
  );
}
